import React from 'react';
import { 
  Calendar, 
  MapPin, 
  DollarSign, 
  ShieldCheck, 
  Clock,
  Award,
  Users,
  Wrench
} from 'lucide-react';

const features = [
  {
    icon: Calendar,
    title: 'Bookings on Your Schedule',
    description: 'Same-day or next-day appointments available. We work around your busy schedule.',
  },
  {
    icon: MapPin,
    title: 'Local Company',
    description: 'Based in Philadelphia and Cherry Hill NJ, serving 15+ locations across PA and NJ.',
  },
  {
    icon: DollarSign,
    title: "Rates That Won't Break the Budget",
    description: 'Fair pricing following UASA Blue Book guidelines. Service call fee varies by brand & model.',
  },
  {
    icon: ShieldCheck,
    title: 'Safety Comes First',
    description: 'All technicians are experienced professionals who undergo thorough background checks.',
  },
];

const stats = [
  { number: '15+', label: 'Service Locations', icon: MapPin },
  { number: '10K+', label: 'Repairs Completed', icon: Wrench },
  { number: '4.9', label: 'Customer Rating', icon: Award },
  { number: '7/7', label: 'Days Available', icon: Clock },
];

const WhyChooseUs = () => {
  return (
    <section data-testid="why-choose-section" className="py-20 md:py-32 bg-secondary/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-4">
            Why BBM?
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Why Choose BBM Appliances?
          </h2>
          <p className="text-lg text-muted-foreground">
            We manage over 15 appliance repair locations, hiring only experienced professionals 
            who work exclusively for us.
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <div
              key={index}
              data-testid={`stat-${index}`}
              className="bg-white rounded-2xl p-6 text-center shadow-sm border border-border"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-xl mb-4">
                <stat.icon className="w-6 h-6 text-primary" />
              </div>
              <div className="stat-number text-3xl font-bold text-primary mb-1">{stat.number}</div>
              <p className="text-sm text-muted-foreground font-medium">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              data-testid={`feature-${index}`}
              className="trust-badge flex gap-6 p-8 bg-white rounded-2xl border border-border"
            >
              <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
              </div>
              <div>
                <h3 className="font-heading font-semibold text-xl text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Banner */}
        <div className="mt-16 bg-primary rounded-3xl p-8 md:p-12 text-white">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <Users className="w-8 h-8" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-2xl mb-1">Our Promise to You</h3>
                <p className="text-white/80">
                  On-time arrival, guaranteed work, and protection for both clients and technicians.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="px-4 py-2 bg-white/20 rounded-full">On-Time Guarantee</span>
              <span className="px-4 py-2 bg-white/20 rounded-full">Work Guaranteed</span>
              <span className="px-4 py-2 bg-white/20 rounded-full">Quality Service</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
